import { useEffect, useRef, useState } from 'react';

/**
 * A bar that appears when the browser loses the network.
 *
 * On a low-end Android over a patchy connection, requests fail constantly. The
 * difference between "the app is broken" and "you are offline" is the whole
 * difference in whether a student trusts it, and the browser already knows
 * which one it is.
 *
 * It says only what is true. Nothing is queued: an answer typed while offline
 * is not sent until the student presses the button again, and the bar used to
 * promise it would "catch up", which it never did. What was already answered
 * is on the server and stays there.
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <p role="status" className="bg-warn px-4 py-1.5 text-center text-sm font-medium text-on-warn">
      You are offline. Your work so far is saved, but new answers and questions cannot be sent until
      you are back online.
    </p>
  );
}

/**
 * Runs `callback` when the browser comes back online, so a screen that failed
 * to load while offline loads itself again rather than sitting on "Could not
 * reach the server" until somebody thinks to refresh.
 */
export function useOnReconnect(callback: () => void): void {
  const latest = useRef(callback);
  useEffect(() => {
    latest.current = callback;
  });

  useEffect(() => {
    const handle = () => latest.current();
    window.addEventListener('online', handle);
    return () => window.removeEventListener('online', handle);
  }, []);
}
